import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
  Menu,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PendingRoundedIcon from "@mui/icons-material/PendingRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useStateContext } from "contexts/ContextProvider";
import api from "services/api";
import dayjs from "dayjs";
import { Invoice } from "./InvoiceItem";
import { formatAmount } from "helpers/ServiceHelpers";
import {
  parseAddress,
  mergeOPIByLanguage,
  generateItemsFromServices,
} from "helpers/InvoiceHelpers";

const LETTER_WIDTH_MM = 215.9;
const LETTER_HEIGHT_MM = 279.4;
const WAIT_MS = 2000; // requested buffer between render and capture
const EXPORT_SCALE = 8; // higher scale for sharper PDFs

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const downloadFile = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Cleanup the URL object to free memory
  setTimeout(() => URL.revokeObjectURL(url), 100);
};

const mapInvoiceToProps = (
  invoice,
  options = {},
  mileageRates = [],
  splitMileage = false,
  keepOPIDetailed = true,
) => {
  const firstPageRows = options.firstPageRows || 11;
  const otherPageRows = options.otherPageRows || 11;
  const rowSpacing = options.rowSpacing || 0.09;

  // Build items list using shared utility
  let items = generateItemsFromServices(
    invoice?.Services || [],
    splitMileage,
    mileageRates,
  );

  // Handle OPI merging if requested (only merge if keepOPIDetailed is false)
  if (!keepOPIDetailed) {
    items = mergeOPIByLanguage(items);
  }

  return {
    pagePadding: "1.5cm",
    invoiceNumber: invoice.Number,
    dateOfIssue: dayjs(invoice.Date).format("MM-DD-YYYY"),
    dueDate: dayjs(invoice.DueDate).format("MM-DD-YYYY"),
    clientName: invoice.Client?.Account,
    clientAddress: parseAddress(invoice.Client?.MainAddress),
    purchaseOrders: invoice.PurchaseOrder,
    items: items,
    subtotal: formatAmount(invoice.Amount),
    discount: "$0.00",
    total: formatAmount(invoice.Amount),
    notes: invoice.Note,
    terms:
      "In the event of late payment, a 10% late fee will be added after 30 business days of receiving the invoice.",
    itemsPerFirstPage: firstPageRows,
    itemsPerPage: otherPageRows,
    rowSpacing: `${rowSpacing}cm`,
    noBorderRadius: true,
  };
};

const InvoiceExport = () => {
  const theme = useTheme();
  const { overlayData, setOverlayName } = useStateContext();
  const hiddenContainerRef = useRef(null);

  const invoiceList = useMemo(() => overlayData?.invoices || [], [
    overlayData?.invoices,
  ]);
  const exportOptions = useMemo(() => overlayData?.exportOptions || [], [
    overlayData?.exportOptions,
  ]);

  const getInvoiceKey = (invoice, fallback) =>
    invoice?.id ?? invoice?.Number ?? invoice?.number ?? fallback;

  const [currentInvoiceProps, setCurrentInvoiceProps] = useState(null);
  const [progressText, setProgressText] = useState("Starting export…");
  const [progressValue, setProgressValue] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [mileageRates, setMileageRates] = useState([]);
  const [fullInvoicesData, setFullInvoicesData] = useState({});

  // State to track split mileage and OPI options per invoice
  const [invoiceSettings, setInvoiceSettings] = useState(() =>
    invoiceList.reduce((acc, inv) => {
      const key = getInvoiceKey(inv, inv.Number);
      acc[key] = {
        splitMileage: true,
        generateBothOPI: false, // When true, generates both detailed and merged versions
      };
      return acc;
    }, {}),
  );

  const [statuses, setStatuses] = useState(() =>
    invoiceList.map((inv, idx) => ({
      key: getInvoiceKey(inv, idx),
      number: inv.Number,
      status: "pending",
      message: null,
    })),
  );

  const updateStatus = useCallback((key, status, message) => {
    setStatuses((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, status, message: message || null } : item,
      ),
    );
  }, []);
  const toggleInvoiceSetting = useCallback((key, setting) => {
    setInvoiceSettings((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [setting]: !prev[key]?.[setting],
      },
    }));
  }, []);

  // Check if an invoice has OPI services
  const hasOPIServices = useCallback(
    (invoiceMeta) => {
      const key = getInvoiceKey(invoiceMeta, invoiceMeta?.Number);
      const fullInvoice = fullInvoicesData[key];
      if (!fullInvoice?.Services) return false;
      return fullInvoice.Services.some(
        (service) => service.InterpretationType === "OPI",
      );
    },
    [fullInvoicesData],
  );

  // Fetch full invoice data and mileage rates on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [mileageResponse, ...invoiceResponses] = await Promise.all([
          api.get("/mileageRates"),
          ...invoiceList.map((inv) => api.get(`/invoices/${inv.id}`)),
        ]);

        setMileageRates(mileageResponse.data || []);

        const invoicesMap = {};
        invoiceResponses.forEach((response, index) => {
          const inv = invoiceList[index];
          const key = getInvoiceKey(inv, inv.Number);
          invoicesMap[key] = response.data;
        });
        setFullInvoicesData(invoicesMap);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    if (!invoiceList.length) return;
    fetchData();
  }, [invoiceList]);

  const handleBeforeUnload = useCallback((e) => {
    e.preventDefault();
    e.returnValue = "PDF generation in progress";
  }, []);

  useEffect(() => {
    if (isGenerating) {
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [isGenerating, handleBeforeUnload]);

  const generatePdfBlob = async (invoiceNumber, clientName) => {
    const host = hiddenContainerRef.current;
    if (!host) throw new Error("Render container missing");

    const pageElements = Array.from(host.children);
    if (!pageElements.length) throw new Error("No pages to export");

    const invoiceItems = host.querySelectorAll(".InvoiceItem");
    const originalStyles = [];
    invoiceItems.forEach((item) => {
      originalStyles.push(item.style.borderRadius);
      item.style.borderRadius = "0";
    });

    const { default: html2canvas } = await import("html2canvas");
    const jsPDF = (await import("jspdf")).default;

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [LETTER_WIDTH_MM, LETTER_HEIGHT_MM],
    });

    for (let i = 0; i < pageElements.length; i++) {
      const pageElement = pageElements[i];

      const canvas = await html2canvas(pageElement, {
        scale: EXPORT_SCALE,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageSmoothingEnabled: true,
      });

      const imgData = canvas.toDataURL("image/png");

      if (i > 0) {
        pdf.addPage([LETTER_WIDTH_MM, LETTER_HEIGHT_MM], "portrait");
      }

      pdf.addImage(
        imgData,
        "JPEG",
        0,
        0,
        LETTER_WIDTH_MM,
        LETTER_HEIGHT_MM,
        undefined,
        "FAST",
      );

      const anchors = pageElement.querySelectorAll(
        'a[href^="mailto:"], a[href^="http://"], a[href^="https://"], a[href^="tel:"]',
      );
      if (anchors.length) {
        const pageRect = pageElement.getBoundingClientRect();
        const mmPerPxX = LETTER_WIDTH_MM / pageRect.width;
        const mmPerPxY = LETTER_HEIGHT_MM / pageRect.height;

        anchors.forEach((anchor) => {
          const rect = anchor.getBoundingClientRect();
          const href = anchor.getAttribute("href");
          if (!href) return;

          const xMm = (rect.left - pageRect.left) * mmPerPxX;
          const yMm = (rect.top - pageRect.top) * mmPerPxY;
          const wMm = rect.width * mmPerPxX;
          const hMm = rect.height * mmPerPxY;

          if (wMm > 0 && hMm > 0) {
            pdf.link(xMm, yMm, wMm, hMm, { url: href });
          }
        });
      }
    }

    invoiceItems.forEach((item, index) => {
      item.style.borderRadius = originalStyles[index];
    });

    return pdf.output("blob");
  };

  const exportInvoiceToPdf = useCallback(async (invoiceNumber, clientName, suffix = "") => {
    const host = hiddenContainerRef.current;
    if (!host) throw new Error("Render container missing");
    
    const pageElements = Array.from(host.children);
    if (!pageElements.length) throw new Error("No pages to export");

    const invoiceItems = host.querySelectorAll(".InvoiceItem");
    const originalStyles = [];
    invoiceItems.forEach((item) => {
      originalStyles.push(item.style.borderRadius);
      item.style.borderRadius = "0";
    });

    const { default: html2canvas } = await import("html2canvas");
    const jsPDF = (await import("jspdf")).default;

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [LETTER_WIDTH_MM, LETTER_HEIGHT_MM],
    });

    for (let i = 0; i < pageElements.length; i++) {
      const pageElement = pageElements[i];

      const canvas = await html2canvas(pageElement, {
        scale: EXPORT_SCALE,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageSmoothingEnabled: true,
      });

      const imgData = canvas.toDataURL("image/png");

      if (i > 0) {
        pdf.addPage([LETTER_WIDTH_MM, LETTER_HEIGHT_MM], "portrait");
      }

      pdf.addImage(
        imgData,
        "JPEG",
        0,
        0,
        LETTER_WIDTH_MM,
        LETTER_HEIGHT_MM,
        undefined,
        "FAST",
      );

      const anchors = pageElement.querySelectorAll(
        'a[href^="mailto:"], a[href^="http://"], a[href^="https://"], a[href^="tel:"]',
      );
      if (anchors.length) {
        const pageRect = pageElement.getBoundingClientRect();
        const mmPerPxX = LETTER_WIDTH_MM / pageRect.width;
        const mmPerPxY = LETTER_HEIGHT_MM / pageRect.height;

        anchors.forEach((anchor) => {
          const rect = anchor.getBoundingClientRect();
          const href = anchor.getAttribute("href");
          if (!href) return;

          const xMm = (rect.left - pageRect.left) * mmPerPxX;
          const yMm = (rect.top - pageRect.top) * mmPerPxY;
          const wMm = rect.width * mmPerPxX;
          const hMm = rect.height * mmPerPxY;

          if (wMm > 0 && hMm > 0) {
            pdf.link(xMm, yMm, wMm, hMm, { url: href });
          }
        });
      }
    }

    invoiceItems.forEach((item, index) => {
      item.style.borderRadius = originalStyles[index];
    });

    const pdfBlob = pdf.output("blob");
    const fileName = suffix
      ? `Invoice ${invoiceNumber} ${clientName}${suffix}.pdf`
      : `Invoice ${invoiceNumber} ${clientName}.pdf`;
    downloadFile(pdfBlob, fileName);
  }, []);

  const generateOne = useCallback(async (meta, index, total) => {
    const statusKey = getInvoiceKey(meta, meta?.Number);
    updateStatus(statusKey, "working", null);
    setProgressText(`Fetching invoice ${meta.Number} (${index + 1}/${total})…`);

    if (!meta.id) {
      throw new Error("Missing invoice id for export");
    }

    const { data: invoice } = await api.get(`/invoices/${meta.id}`);
    const settings = invoiceSettings[statusKey] || {
      splitMileage: false,
      generateBothOPI: false,
    };

    // If generateBothOPI is true, export two versions
    if (settings.generateBothOPI) {
      // Generate detailed (OPI not merged) version
      const propsDetailed = mapInvoiceToProps(
        invoice,
        exportOptions,
        mileageRates,
        settings.splitMileage,
        true,
      );
      setCurrentInvoiceProps(propsDetailed);
      await delay(WAIT_MS);
      setProgressText(
        `Rendering detailed invoice ${meta.Number} (${index + 1}/${total})…`,
      );
      await exportInvoiceToPdf(
        invoice.Number,
        invoice.Client?.Account || "Client",
        " Detailed",
      );

      // Generate merged (OPI merged) version
      const propsMerged = mapInvoiceToProps(
        invoice,
        exportOptions,
        mileageRates,
        settings.splitMileage,
        false,
      );
      setCurrentInvoiceProps(propsMerged);
      await delay(WAIT_MS);
      setProgressText(
        `Rendering merged invoice ${meta.Number} (${index + 1}/${total})…`,
      );
      await exportInvoiceToPdf(
        invoice.Number,
        invoice.Client?.Account || "Client",
      );
    } else {
      // Generate single version (merged by default)
      const props = mapInvoiceToProps(
        invoice,
        exportOptions,
        mileageRates,
        settings.splitMileage,
        false,
      );
      setCurrentInvoiceProps(props);
      await delay(WAIT_MS);
      setProgressText(
        `Rendering invoice ${meta.Number} (${index + 1}/${total})…`,
      );
      await exportInvoiceToPdf(
        invoice.Number,
        invoice.Client?.Account || "Client",
      );
    }

    updateStatus(statusKey, "done", null);
  }, [
    invoiceSettings,
    updateStatus,
    exportOptions,
    mileageRates,
    exportInvoiceToPdf,
  ]);

  const generateAll = useCallback(async () => {
    if (!invoiceList.length) return;
    setIsGenerating(true);
    setProgressValue(0);
    setProgressText("");

    try {
      for (let i = 0; i < invoiceList.length; i++) {
        const meta = invoiceList[i];
        try {
          await generateOne(meta, i, invoiceList.length);
        } catch (err) {
          console.error("Export failed", err);
          const statusKey = getInvoiceKey(meta, meta?.Number);
          updateStatus(statusKey, "error", err?.message || "Failed to export");
        }

        const percent = Math.round(((i + 1) / invoiceList.length) * 100);
        setProgressValue(percent);
        setProgressText(
          `Generated ${i + 1}/${invoiceList.length} invoices (${percent}%)`,
        );
        await delay(WAIT_MS);
      }
    } finally {
      setIsGenerating(false);
      setProgressText("Done");
    }
  }, [invoiceList, updateStatus, generateOne]);

  const generateAllAsZip = useCallback(async () => {
    if (!invoiceList.length) return;
    setIsGenerating(true);
    setProgressValue(0);
    setProgressText("Starting export…");
    setMenuAnchor(null);

    try {
      const JSZip = (await import("jszip")).default;
      const zip = new JSZip();
      const pdfFolder = zip.folder("Invoices");

      for (let i = 0; i < invoiceList.length; i++) {
        const meta = invoiceList[i];
        try {
          const statusKey = getInvoiceKey(meta, meta?.Number);
          updateStatus(statusKey, "working", null);
          setProgressText(
            `Fetching invoice ${meta.Number} (${i + 1}/${invoiceList.length})…`,
          );

          if (!meta.id) {
            throw new Error("Missing invoice id for export");
          }

          const { data: invoice } = await api.get(`/invoices/${meta.id}`);
          const settings = invoiceSettings[statusKey] || {
            splitMileage: false,
            generateBothOPI: false,
          };

          // If generateBothOPI is true, export two versions
          if (settings.generateBothOPI) {
            // Generate detailed (OPI not merged) version
            const propsDetailed = mapInvoiceToProps(
              invoice,
              exportOptions,
              mileageRates,
              settings.splitMileage,
              true,
            );
            setCurrentInvoiceProps(propsDetailed);
            await delay(WAIT_MS);
            setProgressText(
              `Rendering detailed invoice ${meta.Number} (${i + 1}/${invoiceList.length})…`,
            );
            const pdfBlobDetailed = await generatePdfBlob(
              invoice.Number,
              invoice.Client?.Account || "Client",
            );
            const fileNameDetailed = `Invoice ${invoice.Number} ${invoice.Client?.Account || "Client"} Detailed.pdf`;
            pdfFolder?.file(fileNameDetailed, pdfBlobDetailed);

            // Generate merged (OPI merged) version
            const propsMerged = mapInvoiceToProps(
              invoice,
              exportOptions,
              mileageRates,
              settings.splitMileage,
              false,
            );
            setCurrentInvoiceProps(propsMerged);
            await delay(WAIT_MS);
            setProgressText(
              `Rendering merged invoice ${meta.Number} (${i + 1}/${invoiceList.length})…`,
            );
            const pdfBlobMerged = await generatePdfBlob(
              invoice.Number,
              invoice.Client?.Account || "Client",
            );
            const fileNameMerged = `Invoice ${invoice.Number} ${invoice.Client?.Account || "Client"}.pdf`;
            pdfFolder?.file(fileNameMerged, pdfBlobMerged);
          } else {
            // Generate single version (merged by default)
            const props = mapInvoiceToProps(
              invoice,
              exportOptions,
              mileageRates,
              settings.splitMileage,
              false,
            );
            setCurrentInvoiceProps(props);
            await delay(WAIT_MS);
            setProgressText(
              `Rendering invoice ${meta.Number} (${i + 1}/${invoiceList.length})…`,
            );
            const pdfBlob = await generatePdfBlob(
              invoice.Number,
              invoice.Client?.Account || "Client",
            );
            const fileName = `Invoice ${invoice.Number} ${invoice.Client?.Account || "Client"}.pdf`;
            pdfFolder?.file(fileName, pdfBlob);
          }

          updateStatus(statusKey, "done", null);
        } catch (err) {
          console.error("Export failed", err);
          const statusKey = getInvoiceKey(meta, meta?.Number);
          updateStatus(statusKey, "error", err?.message || "Failed to export");
        }

        const percent = Math.round(((i + 1) / invoiceList.length) * 100);
        setProgressValue(percent);
        setProgressText(
          `Generated ${i + 1}/${invoiceList.length} invoices (${percent}%)`,
        );
        await delay(WAIT_MS);
      }

      // Generate and download zip file
      setProgressText("Creating ZIP file…");
      const zipBlob = await zip.generateAsync({ type: "blob" });
      downloadFile(zipBlob, `Invoices_${dayjs().format("YYYY-MM-DD")}.zip`);

      setProgressValue(100);
      setProgressText("Done");
    } catch (error) {
      console.error("ZIP generation failed", error);
      setProgressText(`Error: ${error.message || "Failed to generate ZIP"}`);
    } finally {
      setIsGenerating(false);
    }
  }, [invoiceList, exportOptions, updateStatus, invoiceSettings, mileageRates]);

  const handleGenerateSingle = async (meta) => {
    setIsGenerating(true);
    setProgressValue(0);
    setProgressText(`Generating invoice ${meta.Number}…`);

    try {
      await generateOne(meta, 0, 1);
      setProgressValue(100);
      setProgressText("Done");
    } catch (err) {
      console.error("Export failed", err);
      const statusKey = getInvoiceKey(meta, meta?.Number);
      updateStatus(statusKey, "error", err?.message || "Failed to export");
    } finally {
      setIsGenerating(false);
    }
  };

  // Removed automatic generation on mount - now only manual via button click

  const handleClose = () => {
    if (isGenerating) return; // block closing during generation
    setOverlayName("");
  };

  return (
    <Box
      bgcolor="#fff"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.alt,
          width: "80%",
          height: "85%",
          minWidth: "600px",
          maxWidth: "1400px",
          display: "flex",
          borderRadius: "1.5rem",
          justifyContent: "flex-start",
          alignItems: "stretch",
          flexDirection: "column",
          color: theme.palette.text.primary,
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            p: ".75rem 2rem",
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              color="primary"
              size="large"
              disabled={isGenerating}
              onClick={handleClose}
            >
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>
            <Typography variant="h2" fontWeight="bold">
              Export Invoices
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<CloseRoundedIcon />}
              onClick={handleClose}
              disabled={isGenerating}
            >
              Close
            </Button>
            <Box sx={{ width: 8 }} />
            <Button
              variant="contained"
              color="primary"
              startIcon={<PictureAsPdfRoundedIcon />}
              onClick={generateAll}
              disabled={isGenerating || !invoiceList.length}
              sx={{
                borderTopLeftRadius: "12px",
                borderBottomLeftRadius: "12px",
                borderTopRightRadius: "0px",
                borderBottomRightRadius: "0px",
              }}
            >
              {isGenerating ? "Generating…" : "Generate All"}
            </Button>
            <Box sx={{ width: 2 }} />
            <Button
              variant="contained"
              color="primary"
              //endIcon={<KeyboardArrowDownRoundedIcon />}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              disabled={isGenerating || !invoiceList.length}
              sx={{
                borderTopLeftRadius: "0px",
                borderBottomLeftRadius: "0px",
                borderTopRightRadius: "12px",
                borderBottomRightRadius: "12px",
                minWidth: "auto",
                px: 1,
              }}
            >
              ▼
            </Button>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem onClick={generateAllAsZip}>
                Generate 1 ZIP File
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box sx={{ px: 3, pb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{ height: 12, borderRadius: 8, mb: 1 }}
          />
          <Typography variant="body1" color="text.secondary">
            {progressText}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: "1.2fr 1fr",
            gap: 2,
            px: 3,
            pb: 3,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              bgcolor: theme.palette.background.default,
              borderRadius: "1rem",
              p: 2,
              overflow: "auto",
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="h4" fontWeight={600} gutterBottom>
              Queue
            </Typography>
            <Stack spacing={1.25}>
              {invoiceList.map((inv) => {
                const statusKey = getInvoiceKey(inv, inv?.Number);
                const status = statuses.find((s) => s.key === statusKey) || {
                  status: "pending",
                };
                const icon =
                  status.status === "done" ? (
                    <CheckCircleRoundedIcon color="success" fontSize="small" />
                  ) : status.status === "error" ? (
                    <ErrorOutlineRoundedIcon color="error" fontSize="small" />
                  ) : status.status === "working" ? (
                    <PendingRoundedIcon color="warning" fontSize="small" />
                  ) : (
                    <PendingRoundedIcon color="disabled" fontSize="small" />
                  );

                return (
                  <Box
                    key={statusKey || inv.Number}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "10px",
                      border: `1px solid ${theme.palette.divider}`,
                      p: 1.25,
                      bgcolor: theme.palette.background.alt,
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {icon}
                        <Box>
                          <Typography variant="h5" fontWeight={600}>
                            {inv.Client?.Account || "Client"} Invoice{" "}
                            {inv.Number}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {status.status === "error"
                              ? status.message || "Failed"
                              : status.status === "done"
                                ? "Ready"
                                : status.status === "working"
                                  ? "Generating…"
                                  : "Pending"}
                          </Typography>
                        </Box>
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleGenerateSingle(inv)}
                        disabled={isGenerating}
                      >
                        Generate
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        pl: 4,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              invoiceSettings[statusKey]?.splitMileage || false
                            }
                            onChange={() =>
                              toggleInvoiceSetting(statusKey, "splitMileage")
                            }
                            disabled={isGenerating}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            Split the mileage on Interpretation Assignments
                          </Typography>
                        }
                      />
                      {hasOPIServices(inv) && (
                        <>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ pl: 0, mt: -0.5 }}
                          >
                            OPI Assignments Details
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={
                                  invoiceSettings[statusKey]?.generateBothOPI ||
                                  false
                                }
                                onChange={() =>
                                  toggleInvoiceSetting(
                                    statusKey,
                                    "generateBothOPI",
                                  )
                                }
                                disabled={isGenerating}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2">
                                Generate both detailed and merged versions
                              </Typography>
                            }
                          />
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          <Box
            sx={{
              position: "relative",
              borderRadius: "1rem",
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: theme.palette.background.default,
              minHeight: 0,
            }}
          >
            <Box
              sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
            >
              <Typography variant="h4" fontWeight={600}>
                Preview staging area
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This area renders each invoice off-screen before export. It
                stays hidden during generation.
              </Typography>
            </Box>
            <Box
              ref={hiddenContainerRef}
              sx={{
                position: "absolute",
                top: 0,
                left: "-99999px",
                opacity: 0,
                pointerEvents: "none",
              }}
            >
              {currentInvoiceProps && <Invoice {...currentInvoiceProps} />}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default InvoiceExport;
